import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Sparkles, Play, Heart, Repeat, Eye, Trophy, Crown, Share2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const genreColors = {
  platformer: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  fps: 'bg-red-500/20 text-red-400 border-red-500/30',
  rpg: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  puzzle: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  simulation: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

export default function FeaturedGameSpotlight({ variant = 'landing' }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const autoRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/gallery/featured`);
        if (!mounted) return;
        setData(res.data);
        setActiveIdx(Math.max(0, res.data?.rotation_index || 0));
      } catch (e) {
        if (mounted) setData(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const pool = data?.pool && Array.isArray(data.pool) && data.pool.length > 0
    ? data.pool
    : (data?.featured ? [data.featured] : []);

  // Auto-advance every 8s if there are multiple entries
  useEffect(() => {
    if (paused || pool.length <= 1) return;
    autoRef.current = setInterval(() => {
      setActiveIdx((i) => (i + 1) % pool.length);
    }, 8000);
    return () => { if (autoRef.current) clearInterval(autoRef.current); };
  }, [paused, pool.length]);

  const next = () => setActiveIdx((i) => (i + 1) % Math.max(pool.length, 1));
  const prev = () => setActiveIdx((i) => (i - 1 + Math.max(pool.length, 1)) % Math.max(pool.length, 1));

  if (loading) {
    return (
      <div data-testid="featured-spotlight-loading" className="rounded-2xl border border-[#1E1E24] bg-[#0A0A0B] p-8 animate-pulse">
        <div className="h-4 w-32 bg-white/5 rounded mb-6" />
        <div className="h-8 w-2/3 bg-white/5 rounded mb-4" />
        <div className="h-4 w-1/2 bg-white/5 rounded" />
      </div>
    );
  }

  const featured = pool[activeIdx] || data?.featured;
  const period = data?.period || 'week';

  if (!featured) {
    // Friendly empty-state CTA still nudges sign-up + publishing
    return (
      <div
        data-testid="featured-spotlight-empty"
        className="relative overflow-hidden rounded-2xl border border-[#1E1E24] bg-gradient-to-br from-[#0A0A0B] via-[#0A0A0B] to-[#06B6D4]/5 p-8 md:p-10"
      >
        <div className="absolute -top-16 -right-16 w-72 h-72 bg-[#06B6D4]/10 rounded-full blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#06B6D4]/10 border border-[#06B6D4]/30 mb-5">
            <Crown className="w-3.5 h-3.5 text-[#06B6D4]" />
            <span className="text-[11px] font-mono tracking-widest uppercase text-[#06B6D4]">
              Featured Game of the Week
            </span>
          </div>
          <h3 className="text-2xl md:text-3xl font-black tracking-tight text-white mb-3">
            Your game could be here.
          </h3>
          <p className="text-sm text-zinc-400 max-w-xl mb-6 leading-relaxed">
            Each week we spotlight the community's top creation, ranked by plays, remixes, and likes.
            Publish your game today and you might be next.
          </p>
          <Button
            onClick={() => navigate('/register')}
            className="bg-white text-black hover:bg-zinc-200 rounded-full px-6"
            data-testid="featured-empty-cta"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Start Creating
          </Button>
        </div>
      </div>
    );
  }

  const genreClass = genreColors[featured.genre] || 'bg-zinc-700/30 text-zinc-300 border-zinc-700/40';
  const playCount = featured.play_count || 0;
  const remixCount = featured.remix_count || 0;
  const likesCount = featured.likes_count || 0;
  const authorName = featured.author?.name || 'Unknown';

  const handlePlay = () => navigate(`/play/${featured.project_id}`);
  const handleRemix = async () => {
    // Remix = an editable copy in YOUR editor — change it partly or wholly.
    try {
      toast.info('Creating your editable copy…');
      const res = await axios.post(`${API}/projects/${featured.project_id}/remix`, {}, { withCredentials: true });
      navigate(`/editor/${res.data.project_id}`);
    } catch (e) {
      if (e.response?.status === 401) { toast.error('Log in to remix games'); navigate('/login'); }
      else toast.error(e.response?.data?.detail || 'Remix failed');
    }
  };
  const handleShare = async (e) => {
    e.stopPropagation();
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/${featured.project_id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied!', { description: 'Rich preview card included.' });
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <div
      data-testid="featured-spotlight"
      className="relative overflow-hidden rounded-2xl border border-[#06B6D4]/30 bg-gradient-to-br from-[#0A0A0B] via-[#0A0A0B] to-[#0E2A30] shadow-2xl shadow-[#06B6D4]/5"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Glow accents */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#06B6D4]/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-32 -left-24 w-96 h-96 bg-purple-500/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative grid md:grid-cols-5 gap-0">
        {/* Visual side */}
        <div
          className="md:col-span-2 relative aspect-video md:aspect-auto md:min-h-[280px] bg-[#06B6D4]/10 border-b md:border-b-0 md:border-r border-[#06B6D4]/20 overflow-hidden cursor-pointer group"
          onClick={handlePlay}
          data-testid="featured-thumbnail"
        >
          {/* Dynamic badges stamped on thumbnail */}
          {(featured.badges || []).length > 0 && (
            <div className="absolute top-2 left-2 z-20 flex flex-col gap-1 pointer-events-none" data-testid="featured-badges">
              {(featured.badges || []).slice(0, 3).map((b, i) => (
                <span
                  key={i}
                  className="text-[9px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-lg"
                  style={{ backgroundColor: b.color, color: '#000' }}
                >
                  {b.label}
                </span>
              ))}
            </div>
          )}
          {featured.thumbnail ? (
            <img
              src={featured.thumbnail}
              alt={featured.name}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <img
              src={`${API}/og/${featured.project_id}.png`}
              alt={featured.name}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const fb = e.currentTarget.parentElement.querySelector('.og-fallback');
                if (fb) fb.style.display = 'flex';
              }}
            />
          )}
          <div
            className="og-fallback w-full h-full absolute inset-0 hidden items-center justify-center"
            style={{
              backgroundImage:
                'linear-gradient(rgba(6,182,212,0.08) 1px,transparent 1px),linear-gradient(90deg,rgba(6,182,212,0.08) 1px,transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          >
            <Trophy className="w-20 h-20 text-[#06B6D4]/40 group-hover:text-[#06B6D4]/70 transition-colors" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-6">
            <div className="bg-white text-black rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 shadow-xl">
              <Play className="w-4 h-4 fill-black" />
              Play Now
            </div>
          </div>
        </div>

        {/* Info side */}
        <div className="md:col-span-3 p-7 md:p-9 flex flex-col">
          <div className="inline-flex self-start items-center gap-2 px-3 py-1.5 rounded-full bg-[#06B6D4]/10 border border-[#06B6D4]/30 mb-5">
            <Crown className="w-3.5 h-3.5 text-[#06B6D4]" />
            <span className="text-[11px] font-mono tracking-widest uppercase text-[#06B6D4]">
              Featured Game of the {period === 'week' ? 'Week' : 'Community'}
            </span>
          </div>

          <h3
            className="text-3xl md:text-4xl font-black tracking-tight text-white mb-2 leading-tight"
            data-testid="featured-title"
          >
            {featured.name}
          </h3>

          <p className="text-sm text-zinc-500 mb-4">
            by <span className="text-zinc-300 font-medium">{authorName}</span>
            {featured.genre && (
              <>
                <span className="mx-2 text-zinc-700">•</span>
                <span className={`px-2 py-0.5 text-[10px] font-mono uppercase border rounded-sm ${genreClass}`}>
                  {featured.genre}
                </span>
              </>
            )}
          </p>

          <p className="text-sm md:text-[15px] text-zinc-400 leading-relaxed mb-6 line-clamp-3">
            {featured.description || 'A standout creation from the PlayMold community. Jump in, play, and remix it your way.'}
          </p>

          {/* Stats */}
          <div className="flex items-center gap-5 mb-6 text-sm">
            <div className="flex items-center gap-1.5 text-zinc-400">
              <Eye className="w-4 h-4 text-[#06B6D4]" />
              <span className="font-mono text-white">{playCount.toLocaleString()}</span>
              <span className="text-xs text-zinc-500">plays</span>
            </div>
            <div className="flex items-center gap-1.5 text-zinc-400">
              <Repeat className="w-4 h-4 text-emerald-400" />
              <span className="font-mono text-white">{remixCount.toLocaleString()}</span>
              <span className="text-xs text-zinc-500">remixes</span>
            </div>
            <div className="flex items-center gap-1.5 text-zinc-400">
              <Heart className="w-4 h-4 text-rose-400" />
              <span className="font-mono text-white">{likesCount.toLocaleString()}</span>
              <span className="text-xs text-zinc-500">likes</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 mt-auto">
            <Button
              onClick={handlePlay}
              className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90 rounded-full px-6"
              data-testid="featured-play-btn"
            >
              <Play className="w-4 h-4 mr-2 fill-black" />
              Play Now
            </Button>
            <Button
              onClick={handleRemix}
              variant="outline"
              className="border-white/15 text-white hover:bg-white/5 rounded-full px-6"
              data-testid="featured-remix-btn"
            >
              <Repeat className="w-4 h-4 mr-2" />
              Remix
            </Button>
            <Button
              onClick={handleShare}
              variant="outline"
              className="border-white/15 text-white hover:bg-white/5 rounded-full px-5"
              data-testid="featured-share-btn"
              title="Copy share link with rich preview"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
            {variant === 'landing' && (
              <Button
                onClick={() => navigate('/gallery')}
                variant="ghost"
                className="text-zinc-400 hover:text-white rounded-full px-4"
                data-testid="featured-explore-btn"
              >
                Explore Gallery →
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Carousel controls — only shown when pool has > 1 game */}
      {pool.length > 1 && (
        <>
          <button
            onClick={prev}
            aria-label="Previous featured game"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 backdrop-blur border border-white/10 text-white hover:bg-black/80 hover:border-[#06B6D4]/50 flex items-center justify-center z-10"
            data-testid="featured-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={next}
            aria-label="Next featured game"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 backdrop-blur border border-white/10 text-white hover:bg-black/80 hover:border-[#06B6D4]/50 flex items-center justify-center z-10"
            data-testid="featured-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10" data-testid="featured-dots">
            {pool.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                aria-label={`Go to featured game ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === activeIdx ? 'w-6 bg-[#06B6D4]' : 'w-1.5 bg-white/30 hover:bg-white/50'
                }`}
                data-testid={`featured-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
